import React, { useEffect, useState } from 'react';
import { useRequest } from 'ahooks';
import { Badge, Button, Card, Empty, InfiniteScroll, SearchBar, Tabs, Tag, Toast } from 'antd-mobile';
import styles from './index.module.scss';
import { AlarmRecordRequest, AlarmRecordResponse, AlarmDetailItem, getAlarmRecord } from '@/api/alarmRecord';
import { TabKeyEnum, TabKeyStateDisplay, AlarmLevel, AlarmLevelDisplay, AlarmLevelColorDisplay } from './util';
import { isEmpty } from 'lodash-es';
import { CopyOutlined } from '@ant-design/icons';

import NoData from '@/assets/images/noData.png';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';

const PAGE_SIZE = 20;

const AlarmRecord = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>(TabKeyEnum.UNRESTORED_ALARM);
  const [filterParams] = useState<AlarmRecordRequest>(() => ({
    ruleInfo: searchParams.get('ruleInfo') || undefined,
    alarmName: searchParams.get('alarmName') || undefined,
    startTime: searchParams.get('startTime') ? Number(searchParams.get('startTime')) : undefined,
    endTime: searchParams.get('endTime') ? Number(searchParams.get('endTime')) : undefined,
    levels: searchParams.get('levels') ? [Number(searchParams.get('levels'))] : undefined,
    deviceName: searchParams.get('deviceName') || undefined,
  }));
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AlarmRecordResponse>();
  const [details, setDetails] = useState<AlarmDetailItem[]>([]);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    document.title = '报警记录';
  }, []);

  const { run, runAsync, loading } = useRequest(
    (currentPage: number) => getAlarmRecord({ ...filterParams, state: activeTab, page: currentPage, size: PAGE_SIZE }),
    {
      defaultParams: [1],
      debounceWait: 100,
      refreshDeps: [activeTab, filterParams],
      refreshDepsAction: () => {
        run(1);
      },
      onBefore: ([currentPage]) => {
        if (currentPage === 1) {
          setDetails([]);
          setHasMore(false);
        }
      },
      onSuccess: (response, [currentPage]) => {
        const currentDetails = response.details ?? [];
        if (currentPage === 1) setData(response);
        setDetails(previous => (currentPage === 1 ? currentDetails : [...previous, ...currentDetails]));
        setPage(currentPage);
        setHasMore(
          response.total == null ? currentDetails.length === PAGE_SIZE : currentPage * PAGE_SIZE < response.total
        );
      },
    }
  );

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const loadMore = async () => {
    await runAsync(page + 1);
  };

  const handleOpenFilter = () => {
    const params: Record<string, string> = {};
    if (filterParams.ruleInfo) params.ruleInfo = filterParams.ruleInfo;
    if (filterParams.alarmName) params.alarmName = filterParams.alarmName;
    if (filterParams.startTime) params.startTime = String(filterParams.startTime);
    if (filterParams.endTime) params.endTime = String(filterParams.endTime);
    if (filterParams.levels?.length) params.levels = String(filterParams.levels[0]);
    if (filterParams.deviceName) params.deviceName = filterParams.deviceName;

    const queryString = new URLSearchParams(params).toString();
    const path = queryString
      ? `/intelligent-platform/alarm/record/filter?${queryString}`
      : '/intelligent-platform/alarm/record/filter';
    navigate(path);
  };

  const handleCopy = (item: AlarmDetailItem) => {
    const alarmLevelText = AlarmLevelDisplay[item.alarmLevel ?? AlarmLevel.LOWEST] || '未知';
    const content = `设备名称：${item.deviceName ?? '-'}\n报警等级：${alarmLevelText}\n报警内容：${
      item.alarmName ?? '-'
    }\n报警时间：${item.alarmTime ? dayjs(item.alarmTime).format('YYYY-MM-DD HH:mm:ss') : '-'}\n恢复时间：${
      item.recoveryTime ? dayjs(item.recoveryTime).format('YYYY-MM-DD HH:mm:ss') : '未恢复'
    }\n持续时长：${item.duration ?? '-'}`;
    navigator.clipboard.writeText(content);
    Toast.show({
      content: '复制成功',
      icon: 'success',
    });
  };

  return (
    <div className={styles.page}>
      <div className={styles.stickyHeader}>
        {/* 头部搜索框 */}
        <div className={styles.header} onClick={handleOpenFilter}>
          <SearchBar placeholder="设置查询条件" />
        </div>

        {/* 未恢复/已恢复 Tab */}
        <Tabs activeKey={activeTab} onChange={handleTabChange} className={styles.tabs}>
          <Tabs.Tab
            title={<Badge content={data?.notRecovered || 0}>{TabKeyStateDisplay[TabKeyEnum.UNRESTORED_ALARM]}</Badge>}
            key={TabKeyEnum.UNRESTORED_ALARM}
          />
          <Tabs.Tab
            title={<Badge content={data?.recovered || 0}>{TabKeyStateDisplay[TabKeyEnum.RESTORED_ALARM]}</Badge>}
            key={TabKeyEnum.RESTORED_ALARM}
          />
        </Tabs>
      </div>

      {/* 最高级/最低级卡片 */}
      <div className={styles.levelCards}>
        <div className={styles.levelCard}>
          <div className={styles.levelTitle}>
            <span className={styles.dot} style={{ backgroundColor: AlarmLevelColorDisplay[AlarmLevel.HIGHEST] }} />
            最高级
          </div>
          <div className={styles.levelValue}>{data?.topLevel ?? '-'}</div>
        </div>
        <div className={styles.levelCard}>
          <div className={styles.levelTitle}>
            <span className={styles.dot} style={{ backgroundColor: AlarmLevelColorDisplay[AlarmLevel.HIGH] }} />
            高级
          </div>
          <div className={styles.levelValue}>{data?.highLevel ?? '-'}</div>
        </div>
      </div>

      {/* 报警记录列表 */}
      <div className={styles.list}>
        {!isEmpty(details) ? (
          <>
            {details.map((item: AlarmDetailItem) => (
              <Card
                key={item.id}
                className={styles.card}
                title={
                  <div className={styles.cardHeader}>
                    <span className={styles.alarmName}>{item.deviceName ?? '-'}</span>
                    <Tag color={AlarmLevelColorDisplay[item.alarmLevel ?? AlarmLevel.LOWEST] || 'default'}>
                      {AlarmLevelDisplay[item.alarmLevel ?? AlarmLevel.LOWEST]}
                    </Tag>
                  </div>
                }
              >
                <div className={styles.cardDetail}>
                  <div className={styles.cardRow}>
                    <span className={styles.label}>报警内容：</span>
                    <span className={styles.value}>{item.alarmName ?? '-'}</span>
                  </div>
                  <div className={styles.cardRow}>
                    <span className={styles.label}>报警时间：</span>
                    <span className={styles.value}>
                      {item.alarmTime ? dayjs(item.alarmTime).format('YYYY-MM-DD HH:mm:ss') : '-'}
                    </span>
                  </div>
                  <div className={styles.cardRow}>
                    <span className={styles.label}>恢复时间：</span>
                    <span className={styles.value}>
                      {item.recoveryTime ? dayjs(item.recoveryTime).format('YYYY-MM-DD HH:mm:ss') : '未恢复'}
                    </span>
                  </div>
                  <div className={styles.cardRowWithCopy}>
                    <span className={styles.label}>持续时长：</span>
                    <span className={styles.value}>{item.duration ?? '-'}</span>
                    <Button
                      size="small"
                      className={styles.copyBtn}
                      onClick={e => {
                        e.stopPropagation();
                        handleCopy(item);
                      }}
                    >
                      <CopyOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
            <InfiniteScroll loadMore={loadMore} hasMore={hasMore} />
          </>
        ) : !loading ? (
          <div className={styles.emptyState}>
            <Empty image={NoData} description="暂无数据" />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AlarmRecord;
